import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import axios from 'axios'

const API = ''

function stripHtml(html) {
    if (!html) return ''
    return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
}

const NavSearch = () => {
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')
    const [results, setResults] = useState(null)
    const [loading, setLoading] = useState(false)
    const inputRef = useRef(null)
    const wrapRef = useRef(null)
    const debounceRef = useRef(null)
    const navigate = useNavigate()

    // Close on outside click
    useEffect(() => {
        const handler = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false)
            }
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [])

    // Focus input when opened
    useEffect(() => {
        if (open && inputRef.current) inputRef.current.focus()
    }, [open])

    const search = useCallback((q) => {
        if (!q.trim()) { setResults(null); return }
        setLoading(true)
        axios.get(`${API}/api/search`, { params: { q: q.trim() } })
            .then(r => setResults(r.data))
            .catch(() => setResults(null))
            .finally(() => setLoading(false))
    }, [])

    const handleChange = (e) => {
        const val = e.target.value
        setQuery(val)
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => search(val), 300)
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && query.trim()) {
            setOpen(false)
            navigate(`/search?q=${encodeURIComponent(query.trim())}`)
        }
        if (e.key === 'Escape') {
            setOpen(false)
        }
    }

    const close = () => { setOpen(false); setQuery(''); setResults(null) }

    const totalResults = results
        ? (results.products?.length || 0) + (results.news?.length || 0) + (results.jobs?.length || 0)
        : 0

    return (
        <div className="v2-nav-search-wrap" ref={wrapRef}>
            {!open && (
                <button
                    className="v2-nav-search-btn"
                    onClick={() => setOpen(true)}
                    aria-label="Search"
                    title="Search"
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                </button>
            )}

            {open && (
                <div className="v2-nav-search-expanded">
                    <input
                        ref={inputRef}
                        type="text"
                        className="v2-nav-search-input"
                        placeholder="Search products, news, jobs..."
                        value={query}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                    />
                    <button className="v2-nav-search-close" onClick={close} aria-label="Close search">×</button>

                    {/* Dropdown results */}
                    {query.trim() && (
                        <div className="v2-nav-search-dropdown">
                            {loading && <div className="v2-search-loading">Searching...</div>}

                            {!loading && results && totalResults === 0 && (
                                <div className="v2-search-empty">No results for "{query}"</div>
                            )}

                            {!loading && results && results.products?.length > 0 && (
                                <div className="v2-search-group">
                                    <div className="v2-search-group-label">Products</div>
                                    {results.products.slice(0, 5).map(p => (
                                        <Link key={p.id} to={`/products/${p.id}`} className="v2-search-item" onClick={close}>
                                            <span className="v2-search-item-title">{p.model || p.modelNumber || p.sku || ''}{p.name ? ` - ${p.name}` : ''}</span>
                                            {p.category_name && <span className="v2-search-item-meta">{p.category_name}</span>}
                                        </Link>
                                    ))}
                                </div>
                            )}

                            {!loading && results && results.news?.length > 0 && (
                                <div className="v2-search-group">
                                    <div className="v2-search-group-label">News</div>
                                    {results.news.slice(0, 3).map(n => (
                                        <Link key={n.id} to={`/news/${n.id}`} className="v2-search-item" onClick={close}>
                                            <span className="v2-search-item-title">{n.title}</span>
                                        </Link>
                                    ))}
                                </div>
                            )}

                            {!loading && results && results.jobs?.length > 0 && (
                                <div className="v2-search-group">
                                    <div className="v2-search-group-label">Careers</div>
                                    {results.jobs.slice(0, 3).map(j => (
                                        <Link key={j.id} to="/careers" className="v2-search-item" onClick={close}>
                                            <span className="v2-search-item-title">{j.title}</span>
                                            <span className="v2-search-item-meta">{[j.department, j.location].filter(Boolean).join(' · ')}</span>
                                        </Link>
                                    ))}
                                </div>
                            )}

                            {!loading && totalResults > 0 && (
                                <Link
                                    to={`/search?q=${encodeURIComponent(query.trim())}`}
                                    className="v2-search-view-all"
                                    onClick={close}
                                >
                                    View all results →
                                </Link>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default NavSearch
